// @ts-nocheck
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useApi } from '../hooks/useApi'
import { useCallHistory } from '../hooks/useCallHistory'
import { useCallFormatters } from '../hooks/useCallFormatters'
import { useOrganizationStore } from '../stores/organizationStore'
import { formatPhoneNumberAsYouType, formatPhoneNumberForSave } from '../utils/phoneNumberHelper'
import { ArrowLeft, User, Phone, Building, Mail, Star, Tag, FileText, History, Save, Trash2, ExternalLink, MessageCircle } from 'lucide-react'
import CallHistoryTable from '../components/CallHistoryTable'

export default function CustomerDetailPage() {
  const { customerId } = useParams()
  const navigate = useNavigate()
  const api = useApi()
  const { formatPhoneNumber } = useCallFormatters()
  const { organization, fetchOrganization } = useOrganizationStore()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [customer, setCustomer] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [customerTags, setCustomerTags] = useState([])

  const [formData, setFormData] = useState({
    phoneNumber: '',
    name: '',
    nameKana: '',
    companyName: '',
    department: '',
    position: '',
    email: '',
    alternativePhone: '',
    customerType: 'individual',
    priority: 'normal',
    tags: [],
    status: 'active',
    notes: ''
  })

  const [newTag, setNewTag] = useState('')
  const [lineUserId, setLineUserId] = useState<string | null>(null)

  // 通話履歴フック
  const {
    callHistory: recentCalls,
    loading: callHistoryLoading,
    fetchCallHistory
  } = useCallHistory({
    customerId: customerId,
    limit: 10,
    autoFetch: false
  })

  // 組織設定から顧客タグを取得
  useEffect(() => {
    const loadOrganizationTags = async () => {
      try {
        const orgData = await fetchOrganization(api)
        console.log('組織設定取得:', orgData)
        if (orgData?.customerTags) {
          console.log('顧客タグ設定:', orgData.customerTags)
          setCustomerTags(orgData.customerTags)
        }
      } catch (err) {
        console.error('組織設定の取得に失敗:', err)
      }
    }
    loadOrganizationTags()
  }, [api, fetchOrganization])

  useEffect(() => {
    if (organization?.customerTags) {
      console.log('組織ストアから顧客タグ更新:', organization.customerTags)
      setCustomerTags(organization.customerTags)
    }
  }, [organization])

  // 顧客データ取得
  useEffect(() => {
    // ページトップにスクロール
    window.scrollTo(0, 0)
    
    if (customerId && customerId !== 'new') {
      loadCustomer()
    } else {
      setLoading(false)
      setIsEditing(true)
    }
  }, [customerId])

  const loadCustomer = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await api.get(`/api/customers/${customerId}`)
      setCustomer(data)
      setFormData({
        phoneNumber: data.phoneNumber || '',
        name: data.name || '',
        nameKana: data.nameKana || '',
        companyName: data.companyName || '',
        department: data.department || '',
        position: data.position || '',
        email: data.email || '',
        alternativePhone: data.alternativePhone || '',
        customerType: data.customerType || 'individual',
        priority: data.priority || 'normal',
        tags: data.tags || [],
        status: data.status || 'active',
        notes: data.notes || ''
      })
      // 通話履歴を取得
      fetchCallHistory({ customerId: customerId, limit: 10 })
      // LINE 紐付けを確認
      try {
        const lineData = await api.get(`/api/line/conversations/by-customer/${customerId}`)
        setLineUserId(lineData.conversation?.lineUserId ?? null)
      } catch {
        // LINE未設定の場合は無視
      }
    } catch (err) {
      console.error('Failed to load customer:', err)
      setError('顧客情報の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  // フォーム変更
  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  // 電話番号のフォーマット処理
  const handlePhoneChange = (e) => {
    const { name, value } = e.target
    const formatted = formatPhoneNumberAsYouType(value)
    setFormData(prev => ({ ...prev, [name]: formatted }))
  }

  // タグ追加
  const handleAddTag = () => {
    if (newTag && !formData.tags.includes(newTag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag]
      }))
      setNewTag('')
    }
  }

  // タグ削除
  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }))
  }

  // 保存
  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)

      // バリデーション
      if (!formData.phoneNumber) {
        setError('電話番号は必須です')
        return
      }

      if (!formData.name) {
        setError('名前は必須です')
        return
      }

      // 電話番号を国際形式に変換
      const phoneNumberInternational = formatPhoneNumberForSave(formData.phoneNumber)
      if (!phoneNumberInternational) {
        setError('有効な電話番号を入力してください')
        return
      }

      // 代替電話番号も国際形式に変換（入力されている場合）
      let alternativePhoneInternational = null
      if (formData.alternativePhone) {
        alternativePhoneInternational = formatPhoneNumberForSave(formData.alternativePhone)
        if (!alternativePhoneInternational) {
          setError('代替電話番号が無効です')
          return
        }
      }

      // 保存データを準備
      const saveData = {
        ...formData,
        phoneNumber: phoneNumberInternational,
        alternativePhone: alternativePhoneInternational
      }

      if (customerId && customerId !== 'new') {
        // 更新
        await api.put(`/api/customers/${customerId}`, saveData)
        setIsEditing(false)
        loadCustomer()
      } else {
        // 新規作成
        const result = await api.post('/api/customers', saveData)
        navigate(`/customers/${result.customerId}`, { replace: true })
      }
    } catch (err) {
      console.error('Failed to save customer:', err)
      if (err.response?.status === 409) {
        setError('この電話番号は既に登録されています')
      } else if (err.response?.data?.error) {
        setError(err.response.data.error)
      } else {
        setError(err.message || '保存中にエラーが発生しました')
      }
    } finally {
      setSaving(false)
    }
  }

  // 削除
  const handleDelete = async () => {
    try {
      setSaving(true)
      await api.delete(`/api/customers/${customerId}`)
      navigate('/customers')
    } catch (err) {
      console.error('Failed to delete customer:', err)
      setError('削除に失敗しました')
    } finally {
      setSaving(false)
      setDeleteConfirm(false)
    }
  }

  // 通話履歴詳細へ遷移
  const handleViewCallDetail = (call) => {
    navigate(`/call-history/${call.callHistoryId}`)
  }

  // 戻る
  const handleBack = () => {
    navigate('/customers')
  }

  // 優先度のラベルと色
  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'vip':
        return { label: 'VIP', className: 'bg-purple-100 text-purple-800' }
      case 'high':
        return { label: '高', className: 'bg-red-100 text-red-800' }
      case 'normal':
        return { label: '通常', className: 'bg-gray-100 text-gray-800' }
      case 'low':
        return { label: '低', className: 'bg-blue-100 text-blue-800' }
      default:
        return { label: priority, className: 'bg-gray-100 text-gray-800' }
    }
  }

  if (loading) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  if (!customer && customerId !== 'new') {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">顧客一覧に戻る</span>
        </button>
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          顧客が見つかりませんでした
        </div>
      </div>
    )
  }

  const isNew = customerId === 'new'
  const priorityBadge = getPriorityBadge(formData.priority)

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* ヘッダー */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">顧客一覧に戻る</span>
          </button>
          <div className="flex items-center gap-2">
            {!isNew && !isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors font-medium"
              >
                編集
              </button>
            )}
            {!isNew && (
              <button
                onClick={() => setDeleteConfirm(true)}
                className="px-4 py-2 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors font-medium"
              >
                削除
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
            <User className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isNew ? '新規顧客登録' : (customer?.name || '顧客詳細')}
            </h1>
            {!isNew && customer && (
              <p className="text-sm text-gray-500 mt-0.5">
                {formatPhoneNumber(customer.phoneNumber)}
              </p>
            )}
          </div>
          {!isNew && (
            <div className="ml-auto flex items-center gap-2">
              {lineUserId && (
                <button
                  onClick={() => navigate(`/line-chat?lineUserId=${lineUserId}`)}
                  className="px-3 py-1 text-sm text-green-700 bg-green-50 rounded-full hover:bg-green-100 transition-colors font-medium flex items-center gap-1.5"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  LINE
                </button>
              )}
              <span className={`px-3 py-1 text-sm rounded-full font-medium ${priorityBadge.className}`}>
                {priorityBadge.label}
              </span>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg shadow-sm">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* メインコンテンツ 2カラムレイアウト */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左カラム: 基本情報 */}
        <div className="lg:col-span-2 space-y-6">
          {/* 基本情報カード */}
          <div className="bg-white rounded-lg shadow p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2 pb-3 border-b border-gray-100">
              <User className="w-4 h-4 text-blue-600" />
              基本情報
            </h2>

            {isEditing ? (
              <div className="space-y-4">
                {/* 電話番号 */}
                <div>
                  <label className="block text-xs text-gray-500 mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handlePhoneChange}
                    disabled={!isNew}
                    placeholder="090-1234-5678"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100"
                  />
                  {!isNew && (
                    <p className="mt-1 text-xs text-gray-500">電話番号は変更できません</p>
                  )}
                </div>

                {/* 名前 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">
                      名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">フリガナ</label>
                    <input
                      type="text"
                      name="nameKana"
                      value={formData.nameKana}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                </div>

                {/* 会社情報 */}
                <div>
                  <label className="block text-xs text-gray-500 mb-2">会社名</label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">部署</label>
                    <input
                      type="text"
                      name="department"
                      value={formData.department}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">役職</label>
                    <input
                      type="text"
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                </div>

                {/* メール・代替電話 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">メールアドレス</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">代替電話番号</label>
                    <input
                      type="tel"
                      name="alternativePhone"
                      value={formData.alternativePhone}
                      onChange={handlePhoneChange}
                      placeholder="03-1234-5678"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                </div>

                {/* 顧客タイプ・優先度 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">顧客タイプ</label>
                    <select
                      name="customerType"
                      value={formData.customerType}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="individual">個人</option>
                      <option value="corporate">法人</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">優先度</label>
                    <select
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="low">低</option>
                      <option value="normal">通常</option>
                      <option value="high">高</option>
                      <option value="vip">VIP</option>
                    </select>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">名前</div>
                    <div className="text-sm font-medium text-gray-900">{customer?.name || '-'}</div>
                    {customer?.nameKana && (
                      <div className="text-xs text-gray-500 mt-0.5">{customer.nameKana}</div>
                    )}
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">電話番号</div>
                    <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      {formatPhoneNumber(customer?.phoneNumber)}
                    </div>
                  </div>
                </div>

                {customer?.companyName && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">会社</div>
                    <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                      <Building className="w-4 h-4 text-gray-400" />
                      {customer.companyName}
                      {customer.department && ` / ${customer.department}`}
                      {customer.position && ` (${customer.position})`}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  {customer?.email && (
                    <div>
                      <div className="text-xs text-gray-500 mb-1">メール</div>
                      <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        {customer.email}
                      </div>
                    </div>
                  )}
                  {customer?.alternativePhone && (
                    <div>
                      <div className="text-xs text-gray-500 mb-1">代替電話番号</div>
                      <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        {formatPhoneNumber(customer.alternativePhone)}
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">顧客タイプ</div>
                    <div className="text-sm font-medium text-gray-900">
                      {customer?.customerType === 'corporate' ? '法人' : '個人'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">ステータス</div>
                    <div className="text-sm font-medium text-gray-900">
                      {customer?.status === 'active' ? 'アクティブ' : '非アクティブ'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* タグ・メモカード */}
          <div className="bg-white rounded-lg shadow p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2 pb-3 border-b border-gray-100">
              <Tag className="w-4 h-4 text-purple-600" />
              タグ・メモ
            </h2>

            {isEditing ? (
              <div className="space-y-4">
                {/* タグ */}
                <div>
                  <label className="block text-xs text-gray-500 mb-2">タグ</label>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      placeholder="新しいタグ"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors"
                    >
                      追加
                    </button>
                  </div>
                  
                  {/* よく使うタグ */}
                  {customerTags.length > 0 && (
                    <div className="mb-3">
                      <div className="text-xs text-gray-400 mb-2">よく使うタグ:</div>
                      <div className="flex flex-wrap gap-2">
                        {customerTags.map((tag, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => {
                              if (!formData.tags.includes(tag)) {
                                setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }))
                              }
                            }}
                            disabled={formData.tags.includes(tag)}
                            className="px-2.5 py-1 text-xs bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            + {tag}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="text-blue-500 hover:text-blue-700 transition-colors ml-1"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* メモ */}
                <div>
                  <label className="block text-xs text-gray-500 mb-2">メモ</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {customer?.tags && customer.tags.length > 0 && (
                  <div>
                    <div className="text-xs text-gray-500 mb-2">タグ</div>
                    <div className="flex flex-wrap gap-2">
                      {customer.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {customer?.notes && (
                  <div>
                    <div className="text-xs text-gray-500 mb-2">メモ</div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{customer.notes}</p>
                  </div>
                )}
                {!customer?.tags?.length && !customer?.notes && (
                  <p className="text-sm text-gray-500">タグ・メモはありません</p>
                )}
              </div>
            )}
          </div>

          {/* 編集時の保存ボタン */}
          {isEditing && (
            <div className="flex justify-end gap-3">
              {!isNew && (
                <button
                  onClick={() => {
                    setIsEditing(false)
                    // フォームデータをリセット
                    if (customer) {
                      setFormData({
                        phoneNumber: customer.phoneNumber || '',
                        name: customer.name || '',
                        nameKana: customer.nameKana || '',
                        companyName: customer.companyName || '',
                        department: customer.department || '',
                        position: customer.position || '',
                        email: customer.email || '',
                        alternativePhone: customer.alternativePhone || '',
                        customerType: customer.customerType || 'individual',
                        priority: customer.priority || 'normal',
                        tags: customer.tags || [],
                        status: customer.status || 'active',
                        notes: customer.notes || ''
                      })
                    }
                  }}
                  disabled={saving}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium transition-colors"
                >
                  キャンセル
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          )}
        </div>

        {/* 右カラム: 通話履歴 */}
        <div className="lg:col-span-1 space-y-6">
          {/* 通話履歴カード */}
          {!isNew && (
            <div className="bg-white rounded-lg shadow p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2 pb-3 border-b border-gray-100">
                <History className="w-4 h-4 text-orange-600" />
                最近の通話
                {recentCalls.length > 0 && (
                  <span className="ml-auto px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                    {recentCalls.length}件
                  </span>
                )}
              </h2>

              {callHistoryLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : recentCalls.length === 0 ? (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg text-sm">
                  通話履歴がありません
                </div>
              ) : (
                <div className="space-y-3">
                  <CallHistoryTable
                    callHistory={recentCalls}
                    displayMode="accordion"
                    onEditMemo={handleViewCallDetail}
                    emptyMessage="通話履歴がありません"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 削除確認ダイアログ */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">顧客を削除</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              「{customer?.name}」を削除してもよろしいですか？この操作は取り消せません。
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(false)}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium transition-colors"
              >
                キャンセル
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium transition-colors"
              >
                {saving ? '削除中...' : '削除'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
